import ResponsiveTable from '../../../ReusableComponents/ResponsiveTable';
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import { MdDownload, MdRefresh } from "react-icons/md";
import { useDispatch } from "react-redux";
import Request from "../../../../Request";
import { logout } from "../../../../actions/AppActions";
import Search from "../../../ReusableComponents/Search";
import "./DashboardReports.css";

const jobStatusText = status => {
    if (status === "0") return "Pendente";
    if (status === "1") return "Executando";
    if (status === "2") return "Concluído";
    if (status === "3") return "Erro";
    return "-";
};

const reportFlagBadge = value => {
    const normalized = String(value || "").toLowerCase();
    const positive = ["sim", "yes", "true", "1", "detectado", "detectada"];
    const negative = ["não", "nao", "no", "false", "0", "-", ""];
    const variant = positive.includes(normalized) ? "danger" : negative.includes(normalized) ? "success" : "warning";
    const label = value == null || value === "" ? "-" : value;
    return <span className={`dashboard-report-badge ${variant}`}>{label}</span>;
};

const reportNumberBadge = value => {
    const number = Number(value || 0);
    return <span className={`dashboard-report-badge ${number > 0 ? "warning" : "success"}`}>{value || 0}</span>;
};

const GeneralCandidatesReport = ({ faculdade_id }) => {
    const dispatch = useDispatch();
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total_pages: 0 });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [job, setJob] = useState(null);
    const [message, setMessage] = useState("");

    const getReport = async (page = 1, searchValue = search) => {
        if (!faculdade_id) return;
        setLoading(true);
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("per_page", 20);
        if (searchValue) params.append("search", searchValue);

        const response = await Request.Get(`faculdade/get_relatorio_geral_candidatos_faculdade/${faculdade_id}?${params.toString()}`);
        setLoading(false);
        if (response === undefined) return;
        if (response.message === "Unauthenticated.") {
            dispatch(logout());
            return;
        }
        if (response.errors) {
            setMessage(response.errors.error || "Não foi possível carregar o relatório.");
            return;
        }

        setRows(response.rows || []);
        setPagination(response.pagination || { current_page: 1, last_page: 1, total_pages: 0 });
        setJob(response.job || null);
    };

    const generateReport = async () => {
        setGenerating(true);
        const response = await Request.Post(`faculdade/generate_relatorio_geral_candidatos_faculdade/${faculdade_id}`, new FormData());
        setGenerating(false);
        if (response === undefined) return;
        if (response.message === "Unauthenticated.") {
            dispatch(logout());
            return;
        }
        if (response.errors) {
            setMessage(response.errors.error || "Não foi possível gerar o relatório.");
            return;
        }
        if (response.message) setMessage(response.message);
        setJob(response.job || null);
        getReport(pagination.current_page || 1);
    };

    const downloadReport = async () => {
        setDownloading(true);
        const response = await Request.Get(`faculdade/download_relatorio_geral_candidatos_faculdade/${faculdade_id}`);
        setDownloading(false);
        if (response === undefined) return;
        if (response.message === "Unauthenticated.") {
            dispatch(logout());
            return;
        }
        if (response.errors) {
            setMessage(response.errors.error || "Não foi possível baixar a planilha.");
            return;
        }
        if (response.path) {
            window.open(response.path, "_blank");
            setMessage("Planilha gerada. O download foi aberto em uma nova aba.");
        } else if (response.message) {
            setMessage(response.message);
        }
    };

    const refreshReport = () => {
        getReport(pagination.current_page || 1, search);
    };

    useEffect(() => {
        getReport(1, "");
    }, [faculdade_id]);

    useEffect(() => {
        if (job == null || (job.status !== "0" && job.status !== "1")) return;
        const interval = setInterval(() => getReport(pagination.current_page || 1), 30000);
        return () => clearInterval(interval);
    }, [job, pagination.current_page]);

    useEffect(() => {
        const timeout = setTimeout(() => getReport(1, search), 450);
        return () => clearTimeout(timeout);
    }, [search]);

    return (
        <div className="dashboard-report-card">
            <div className="dashboard-report-header">
                <div>
                    <h5>Relatório Geral de candidatos</h5>
                    <p>Consolida saídas de tela, desconexões, eventos de IA, IP e geolocalização dos candidatos da instituição.</p>
                    {job != null && <span className="dashboard-report-job">
                        Status do Excel: <b>{jobStatusText(job.status)}</b>
                        {job.total_progresso > 0 && <> ({job.progresso || 0} de {job.total_progresso})</>}
                    </span>}
                    {message && <span className="dashboard-report-job">{message}</span>}
                </div>
                <div className="dashboard-report-actions">
                    <Button variant="light" onClick={refreshReport} disabled={loading}>
                        {loading ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : <MdRefresh style={{ marginRight: 8 }} />}
                        Atualizar
                    </Button>
                    <Button variant="warning" onClick={generateReport} disabled={generating || (job != null && (job.status === "0" || job.status === "1"))}>
                        {generating ? <CircularProgress size={16} style={{ color: "white", marginRight: 8 }} /> : <MdRefresh style={{ marginRight: 8 }} />}
                        Gerar Excel
                    </Button>
                    <Button variant="success" onClick={downloadReport} disabled={downloading}>
                        {downloading ? <CircularProgress size={16} style={{ color: "white", marginRight: 8 }} /> : <MdDownload style={{ marginRight: 8 }} />}
                        Baixar Excel
                    </Button>
                </div>
            </div>

            <div className="dashboard-report-filter">
                <Search
                    hide_class
                    className="dashboard-report-search"
                    placeholder="Nome, e-mail, CPF ou inscrição"
                    ValueChange={value => setSearch(value)}
                />
            </div>

            <div className="table-responsive dashboard-report-table">
                <ResponsiveTable className="table table-striped">
                    <thead>
                        <tr>
                            <th>Nome do candidato</th>
                            <th>Saídas</th>
                            <th>Tempo fora</th>
                            <th>Sites acessados</th>
                            <th>Desconexões</th>
                            <th>Cabelo solto</th>
                            <th>Relógio</th>
                            <th>Fone</th>
                            <th>Microfone não detectável</th>
                            <th>IP</th>
                            <th>Geolocalização</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(item => (
                            <tr key={item.user_id}>
                                <td><b>{item.candidate_name}</b></td>
                                <td>{reportNumberBadge(item.screen_exit_count)}</td>
                                <td><span className="dashboard-report-badge neutral">{item.screen_exit_time || "-"}</span></td>
                                <td className="dashboard-report-sites">{item.visited_sites}</td>
                                <td>{reportNumberBadge(item.disconnect_count)}</td>
                                <td>{reportFlagBadge(item.has_long_hair)}</td>
                                <td>{reportFlagBadge(item.has_smartwatch)}</td>
                                <td>{reportFlagBadge(item.has_headphones)}</td>
                                <td>{reportFlagBadge(item.has_microphone_unavailable)}</td>
                                <td><span className="dashboard-report-badge info">{item.ip || "-"}</span></td>
                                <td><span className="dashboard-report-badge neutral">{item.geolocation || "-"}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </ResponsiveTable>
                {loading && <div className="dashboard-report-loading"><CircularProgress /></div>}
                {!loading && rows.length === 0 && <div className="dashboard-report-empty">Nenhum candidato encontrado para os filtros informados.</div>}
            </div>

            <Pagination
                className={(pagination.last_page === 1 ? "hide" : "") + " hidethis"}
                style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}
                count={pagination.last_page}
                value={pagination.current_page}
                onChange={(_, value) => getReport(value)}
            />
        </div>
    );
};

export default GeneralCandidatesReport;
